import type { ReactNode } from 'react';

import { t } from '@lingui/core/macro';
import { Heading } from 'react-aria-components';

import { Button } from '@/components/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/components/base/modals/modal';

interface ConfirmationModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Rendered between the description and the actions (e.g. a warning callout). */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
  isLoading?: boolean;
  /**
   * Extra classes for the overlay — e.g. to raise the z-index when nesting the
   * confirmation above another layer such as a Drawer (`z-60`).
   */
  className?: string;
}

export const ConfirmationModal = ({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = t`Confirm`,
  cancelLabel = t`Cancel`,
  onConfirm,
  onCancel,
  isDestructive = false,
  isLoading = false,
  className,
}: ConfirmationModalProps) => {
  return (
    <ModalOverlay
      centered
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className={className}
    >
      <Modal>
        <Dialog>
          <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-xl">
            <Heading
              slot="title"
              level={2}
              className="text-lg font-medium text-primary"
            >
              {title}
            </Heading>
            {description && (
              <p className="mt-2 text-sm text-tertiary">{description}</p>
            )}
            {children}
            <div className="mt-6 flex justify-end gap-3">
              <Button color="secondary" onClick={onCancel}>
                {cancelLabel}
              </Button>
              <Button
                color={isDestructive ? 'primary-destructive' : 'primary'}
                onClick={onConfirm}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
